/**
 * QUIL — server-anchored clock (Phase-3 B2 / audit §5 clock-skew rework,
 * approved by Kamrin 2026-07-13).
 *
 * Every synced entity timestamp (updatedAt, tombstone deletedAt) feeds a
 * last-writer-wins merge against stamps written by OTHER devices and against
 * OneDrive's own lastModifiedDateTime. Raw Date.now() makes those comparisons
 * only as good as each device's wall clock. Instead, each device learns its
 * offset from OneDrive's clock — every successful upload response carries the
 * server's lastModifiedDateTime for a write that happened "now" — and stamps
 * entities with server-anchored time. All devices anchor to the same server
 * clock, so LWW becomes skew-immune (within network-latency noise, ≲1s, which
 * is far inside human editing granularity).
 *
 * The learned offset persists in settings (additive field, no schema bump) so
 * a session that hasn't uploaded yet still uses the last-known offset; before
 * any offset is ever learned it is 0 — plain device time, the pre-rework
 * behaviour. Issued stamps are also strictly monotonic within a session, so an
 * offset re-estimate can never hand out a stamp earlier than one already given.
 *
 * Device-LOCAL bookkeeping (sync-queue retry clocks, session records, UI
 * timers) deliberately stays on Date.now() — only synced stamps go through here.
 */

import { db } from './db'

let offsetMs = 0
let persistedOffsetMs = 0
let lastIssued = 0
let loaded = false
// Whether a PRECISE offset is in hand: either persisted from an earlier session or learned from an
// upload response. Until one is, `reportServerDate` may anchor coarsely from a response Date header.
let anchored = false

// Below this, a change in the estimate is network-latency jitter, not skew —
// not worth a settings write.
const PERSIST_THRESHOLD_MS = 2_000

/** Server-anchored, session-monotonic "now" — for every synced entity stamp. */
export function serverNow(): number {
  const t = Date.now() + offsetMs
  lastIssued = t > lastIssued ? t : lastIssued + 1
  return lastIssued
}

/** Load the persisted offset once at startup (safe to call repeatedly). */
export async function initClock(): Promise<void> {
  if (loaded) return
  loaded = true
  const s = await db.settings.get(1)
  if (typeof s?.clockOffsetMs === 'number' && Number.isFinite(s.clockOffsetMs)) {
    offsetMs = s.clockOffsetMs
    persistedOffsetMs = s.clockOffsetMs
    anchored = true
  }
}

/**
 * Feed an authoritative server timestamp for a write that just happened (an
 * upload response's lastModifiedDateTime). Adopts the fresh estimate and
 * persists it when it moved beyond jitter.
 */
export function reportServerTime(iso: string | null | undefined): void {
  if (!iso) return
  const serverMs = new Date(iso).getTime()
  if (!Number.isFinite(serverMs)) return
  anchored = true
  offsetMs = serverMs - Date.now()
  if (Math.abs(offsetMs - persistedOffsetMs) > PERSIST_THRESHOLD_MS) {
    persistedOffsetMs = offsetMs
    // Fire-and-forget: a no-op before the settings row exists (fresh test envs).
    void db.settings.update(1, { clockOffsetMs: offsetMs }).catch(() => {})
  }
}

/**
 * Coarse anchor from a Graph response's `Date` header, used ONLY while no precise offset exists.
 *
 * Without this, a device that has never completed an upload runs at `offsetMs = 0` — plain device
 * time — and everything it stamps before its first successful write keeps whatever skew that device's
 * wall clock had. Nothing re-stamps those rows, and a far-future stamp then wins every later
 * cross-device LWW comparison, permanently (flow-coherence review 2026-09-17, finding #10; the same
 * defect reached app-platform's FIX-REGISTER from the other direction as "the clock is corrected on
 * write but never on read").
 *
 * It deliberately does NOT reuse a read's `lastModifiedDateTime`, which is the obvious-looking fix
 * and is badly wrong: that is the file's modification time, so reading a chapter last touched a
 * fortnight ago would anchor the clock a fortnight into the past. A response `Date` header is the
 * server's current time, which is the only thing on a read that means "now".
 *
 * Second-resolution, so it is a fallback rather than a replacement: the first upload response hands
 * back a millisecond-precise `lastModifiedDateTime` and `reportServerTime` takes over for good. Not
 * persisted for the same reason — it is re-derived from the first Graph response of any session that
 * still lacks a precise offset, which happens long before the writer can type much.
 */
export function reportServerDate(header: string | null | undefined): void {
  if (anchored || !header) return
  const serverMs = new Date(header).getTime()
  if (!Number.isFinite(serverMs)) return
  offsetMs = serverMs - Date.now()
}

/** Test-only: reset the module state between cases.
 * @internal Test seam: resets module state between tests. Never imported by the app.
 */
export function resetClock(): void {
  offsetMs = 0
  persistedOffsetMs = 0
  lastIssued = 0
  loaded = false
  anchored = false
}
